var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { localized, msg } from '@lit/localize';
/**
 * Animated, carto-flavoured loading block shown while a Flow workflow runs.
 *
 * It is a *content* component (no overlay): embed it inside your own modal /
 * container. It cycles through humanized phrases ("Flow is calculating…") to
 * keep the user company during long executions, and — when given a
 * {@link NodeExecutionRead} — surfaces the real node name, message and a
 * localized status badge underneath the animation.
 */
let FlowLoading = class FlowLoading extends LitElement {
    constructor() {
        super(...arguments);
        this.phase = 'running';
        this.nodeExecution = null;
        this.variant = 'primary';
        this.phraseIntervalMs = 2500;
        this.phrases = null;
        this.phraseIndex = 0;
        this.intervalId = null;
    }
    /** Default humanized phrases. Sources are English; FR comes from xliff/fr.xlf. */
    get defaultPhrases() {
        return [
            msg('Flow is calculating…', { id: 'flow.loading.phrase.calculating', desc: 'Flow loader phrase' }),
            msg('Flow is thinking…', { id: 'flow.loading.phrase.thinking', desc: 'Flow loader phrase' }),
            msg('Flow is analyzing…', { id: 'flow.loading.phrase.analyzing', desc: 'Flow loader phrase' }),
            msg('Flow is mapping…', { id: 'flow.loading.phrase.mapping', desc: 'Flow loader phrase' }),
            msg('Flow is optimizing…', { id: 'flow.loading.phrase.optimizing', desc: 'Flow loader phrase' }),
        ];
    }
    get activePhrases() {
        const list = this.phrases && this.phrases.length > 0 ? this.phrases : this.defaultPhrases;
        return list.length > 0 ? list : [''];
    }
    connectedCallback() {
        super.connectedCallback();
        this.startCycle();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.stopCycle();
    }
    updated(changed) {
        if (changed.has('phase') || changed.has('phraseIntervalMs')) {
            this.startCycle();
        }
    }
    startCycle() {
        this.stopCycle();
        // Phrases only cycle while the workflow is actually running.
        if (this.phase !== 'running' || this.phraseIntervalMs <= 0)
            return;
        this.intervalId = setInterval(() => {
            this.phraseIndex = (this.phraseIndex + 1) % this.activePhrases.length;
        }, this.phraseIntervalMs);
    }
    stopCycle() {
        if (this.intervalId !== null) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }
    /** Map a raw execution status string to a localized, human label. */
    statusLabel(status) {
        switch ((status ?? '').toLowerCase()) {
            case 'pending':
                return msg('Pending', { id: 'flow.status.pending', desc: 'Workflow node status' });
            case 'running':
                return msg('In progress', { id: 'flow.status.running', desc: 'Workflow node status' });
            case 'completed':
                return msg('Completed', { id: 'flow.status.completed', desc: 'Workflow node status' });
            case 'failed':
                return msg('Failed', { id: 'flow.status.failed', desc: 'Workflow node status' });
            default:
                return status ?? '';
        }
    }
    statusVariant(status) {
        const s = (status ?? '').toLowerCase();
        if (s === 'completed')
            return 'success';
        if (s === 'failed')
            return 'error';
        if (s === 'running')
            return 'running';
        return 'pending';
    }
    renderScene() {
        return html `
      <div class="scene">
        <svg class="map" viewBox="0 0 140 140" role="img" aria-hidden="true">
          <!-- street grid -->
          <line class="street" x1="20" y1="38" x2="120" y2="38"></line>
          <line class="street" x1="20" y1="102" x2="120" y2="102"></line>
          <line class="street" x1="46" y1="14" x2="46" y2="126"></line>
          <line class="street" x1="94" y1="14" x2="94" y2="126"></line>
          <!-- expanding ping ring -->
          <circle class="ping" cx="70" cy="70" r="26"></circle>
          <!-- isochrone outline being drawn -->
          <circle class="isochrone" cx="70" cy="70" r="40"></circle>
          <!-- pin -->
          <ellipse class="pin-shadow" cx="70" cy="112" rx="10" ry="3"></ellipse>
          <g class="pin">
            <path
              d="M70 50 C61 50 54 57 54 66 C54 78 70 92 70 92 C70 92 86 78 86 66 C86 57 79 50 70 50 Z"
              fill="var(--flow-primary)"
            ></path>
            <circle cx="70" cy="66" r="6" fill="#ffffff"></circle>
          </g>
        </svg>
      </div>
    `;
    }
    renderConnectingScene() {
        return html `
      <div class="scene">
        <svg class="map" viewBox="0 0 140 140" role="img" aria-hidden="true">
          <!-- left endpoint (client) -->
          <circle class="endpoint" cx="30" cy="70" r="7"></circle>
          <!-- dashed connection link -->
          <line class="link" x1="40" y1="70" x2="98" y2="70"></line>
          <!-- packets traveling toward the Flow hub -->
          <circle class="packet" cx="38" cy="70" r="3.5"></circle>
          <circle class="packet p2" cx="38" cy="70" r="3.5"></circle>
          <circle class="packet p3" cx="38" cy="70" r="3.5"></circle>
          <!-- Flow hub with pinging rings -->
          <circle class="hub-ring" cx="104" cy="70" r="12"></circle>
          <circle class="hub" cx="104" cy="70" r="10"></circle>
          <circle cx="104" cy="70" r="4" fill="#ffffff"></circle>
        </svg>
      </div>
    `;
    }
    renderDetail() {
        const node = this.nodeExecution;
        if (!node)
            return nothing;
        const name = node.node_saved?.name;
        const message = node.message;
        const status = node.status;
        return html `
      <div class="detail">
        ${name ? html `<div class="node-name">${name}</div>` : nothing}
        ${message ? html `<div class="node-message">${message}</div>` : nothing}
        ${status
            ? html `<span class="status ${this.statusVariant(status)}">${this.statusLabel(status)}</span>`
            : nothing}
      </div>
    `;
    }
    render() {
        if (this.phase === 'connecting') {
            return html `
        ${this.renderConnectingScene()}
        <div class="phrase" aria-live="polite">
          ${msg('Connecting to Flow…', { id: 'flow.loading.connecting', desc: 'Flow connection (init) state' })}
        </div>
      `;
        }
        const phrases = this.activePhrases;
        const phrase = phrases[this.phraseIndex % phrases.length] ?? '';
        return html `
      ${this.renderScene()}
      ${keyed(this.phraseIndex, html `<div class="phrase" aria-live="polite">${phrase}</div>`)}
      ${this.renderDetail()}
    `;
    }
};
FlowLoading.styles = css `
    :host {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 20px;
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      padding: 16px;
      color: #1f2937;
      font-family: 'Montserrat', system-ui, -apple-system, sans-serif;
      /* default = primary */
      --flow-primary: var(--color-primary, #3b6c4c);
      --flow-primary-soft: rgba(var(--color-primary-rgb, 59, 108, 76), 0.12);
    }

    :host([variant='secondary']) {
      --flow-primary: var(--color-secondary-dark, #6b7280);
      --flow-primary-soft: rgba(var(--color-secondary-dark-rgb, 107, 114, 128), 0.12);
    }

    :host([variant='success']) {
      --flow-primary: var(--color-success, #396527);
      --flow-primary-soft: rgba(var(--color-success-rgb, 57, 101, 39), 0.12);
    }

    :host([variant='warning']) {
      --flow-primary: var(--color-warning, #b0821e);
      --flow-primary-soft: rgba(var(--color-warning-rgb, 176, 130, 30), 0.12);
    }

    :host([variant='danger']) {
      --flow-primary: var(--color-danger, #a72608);
      --flow-primary-soft: rgba(var(--color-danger-rgb, 167, 38, 8), 0.12);
    }

    /* ── Carto animation ───────────────────────────────── */
    .scene {
      position: relative;
      width: 140px;
      height: 140px;
    }

    .map {
      width: 100%;
      height: 100%;
      display: block;
      border-radius: 16px;
      background: linear-gradient(135deg, #f4f7fb 0%, #eef3f8 100%);
      box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.05);
      overflow: visible;
    }

    .street {
      stroke: #d4dde8;
      stroke-width: 3;
      stroke-linecap: round;
    }

    .isochrone {
      fill: var(--flow-primary-soft);
      stroke: var(--flow-primary);
      stroke-width: 2.5;
      stroke-dasharray: 360;
      stroke-dashoffset: 360;
      transform-origin: 70px 70px;
      animation: draw-iso 3.2s ease-in-out infinite;
    }

    .ping {
      fill: none;
      stroke: var(--flow-primary);
      stroke-width: 2;
      transform-origin: 70px 70px;
      animation: ping 2.4s ease-out infinite;
    }

    .pin {
      transform-origin: 70px 78px;
      animation: bob 1.6s ease-in-out infinite;
    }

    .pin-shadow {
      fill: rgba(15, 23, 42, 0.18);
      transform-origin: 70px 112px;
      animation: shadow 1.6s ease-in-out infinite;
    }

    @keyframes draw-iso {
      0% { stroke-dashoffset: 360; opacity: 0; }
      30% { opacity: 1; }
      70% { stroke-dashoffset: 0; opacity: 1; }
      100% { stroke-dashoffset: 0; opacity: 0; }
    }

    @keyframes ping {
      0% { transform: scale(0.3); opacity: 0.7; }
      80% { transform: scale(1.4); opacity: 0; }
      100% { transform: scale(1.4); opacity: 0; }
    }

    @keyframes bob {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-8px); }
    }

    @keyframes shadow {
      0%, 100% { transform: scaleX(1); opacity: 0.5; }
      50% { transform: scaleX(0.6); opacity: 0.25; }
    }

    /* ── Phrase ────────────────────────────────────────── */
    .phrase {
      min-height: 1.5em;
      font-size: 1.05rem;
      font-weight: 600;
      color: var(--flow-primary);
      text-align: center;
      animation: fade-slide 0.45s ease;
    }

    @keyframes fade-slide {
      from { opacity: 0; transform: translateY(6px); }
      to { opacity: 1; transform: translateY(0); }
    }

    /* ── Node detail ───────────────────────────────────── */
    .detail {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      max-width: 320px;
      text-align: center;
    }

    .node-name {
      font-size: 0.95rem;
      font-weight: 600;
      color: #111827;
    }

    .node-message {
      font-size: 0.85rem;
      color: #6b7280;
      line-height: 1.35;
      word-break: break-word;
    }

    .status {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 2px 10px;
      border-radius: 999px;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: capitalize;
    }

    .status::before {
      content: '';
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: currentColor;
    }

    .status.running { color: var(--flow-primary); background: var(--flow-primary-soft); }
    .status.running::before { animation: blink 1s ease-in-out infinite; }
    .status.pending { color: #b45309; background: rgba(245, 158, 11, 0.14); }
    .status.success { color: #15803d; background: rgba(34, 197, 94, 0.14); }
    .status.error { color: #b91c1c; background: rgba(239, 68, 68, 0.14); }

    @keyframes blink {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.3; }
    }

    /* ── Connecting (init) animation ───────────────────── */
    .link {
      stroke: var(--flow-primary-soft);
      stroke-width: 3;
      stroke-linecap: round;
      stroke-dasharray: 4 7;
    }

    .endpoint {
      fill: none;
      stroke: var(--flow-primary);
      stroke-width: 3;
    }

    .hub {
      fill: var(--flow-primary);
    }

    .hub-ring {
      fill: none;
      stroke: var(--flow-primary);
      stroke-width: 2;
      transform-origin: 104px 70px;
      animation: hub-ping 1.8s ease-out infinite;
    }

    .packet {
      fill: var(--flow-primary);
      animation: travel 1.6s ease-in-out infinite;
    }
    .packet.p2 { animation-delay: 0.53s; }
    .packet.p3 { animation-delay: 1.06s; }

    @keyframes travel {
      0% { transform: translateX(0); opacity: 0; }
      15% { opacity: 1; }
      85% { opacity: 1; }
      100% { transform: translateX(66px); opacity: 0; }
    }

    @keyframes hub-ping {
      0% { transform: scale(0.6); opacity: 0.6; }
      100% { transform: scale(2); opacity: 0; }
    }

    @media (prefers-reduced-motion: reduce) {
      .isochrone, .ping, .pin, .pin-shadow, .phrase, .status.running::before,
      .packet, .hub-ring {
        animation: none;
      }
      .isochrone { stroke-dashoffset: 0; opacity: 1; }
    }
  `;
__decorate([
    property({ type: String, reflect: true })
], FlowLoading.prototype, "phase", void 0);
__decorate([
    property({ type: Object })
], FlowLoading.prototype, "nodeExecution", void 0);
__decorate([
    property({ type: String, reflect: true })
], FlowLoading.prototype, "variant", void 0);
__decorate([
    property({ type: Number })
], FlowLoading.prototype, "phraseIntervalMs", void 0);
__decorate([
    property({ type: Array })
], FlowLoading.prototype, "phrases", void 0);
__decorate([
    state()
], FlowLoading.prototype, "phraseIndex", void 0);
FlowLoading = __decorate([
    localized(),
    customElement('flow-loading')
], FlowLoading);
export { FlowLoading };
//# sourceMappingURL=FlowLoading.js.map